<template>
  <div class="create-project">
      <b-form @submit.stop.prevent="handleSubmit(submit)">
        <b-row>
          <b-col>
            <b-card
              v-if="currentStep === 1"
              class="my-2"
            >
              <b-row>
                <h3>Step 1</h3>
              </b-row>

              <b-row>
                <b-col md="12">
                  <BaseInput
                    v-model="project.name"
                    description="Enter name for this project"
                    label="Project Name"
                    rules="required"
                    trim
                  />
                </b-col>

                <b-col md="12">
                  <BaseTextArea
                    v-model="project.description"
                    description="Long description for this project"
                    label="Description"
                    rules="required"
                  />
                </b-col>
              </b-row>

              <b-row>
                <b-col
                  v-for="(service, index) in services"
                  :key="index"
                  md="4"
                >
                  <b-form-group :name="service">
                    <b-form-checkbox
                      v-model="projectScops"
                      :value="service"
                    >
                      {{ service }}
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
              </b-row>

              <hr>
              <b-row align-h="end">
                <b-col md="2">
                  <BaseButton
                    :disabled="invalid"
                    type="button"
                    variant="primary"
                    text="Next"
                    class="d-block w-100"
                    @click="next()"
                  />
                </b-col>
              </b-row>
            </b-card>

            <b-card
              v-if="currentStep === 2"
              class="my-2"
            >
              <b-row>
                <h3>Step 2</h3>
              </b-row>

              <b-row>
                <b-col>
                  <AddressAutoComplete
                    v-model="project.address"
                    label="Project Location"
                    placeholder="Address"
                    rules="required"
                    :disabled="loading"
                    :value="project ? project.address : ''"
                  />
                </b-col>
              </b-row>

              <hr>
              <b-row align-h="end">
                <b-col md="2">
                  <BaseButton
                    type="button"
                    variant="primary"
                    text="Back"
                    class="d-block w-100"
                    :disabled="loading"
                    @click="back()"
                  />
                </b-col>
                <b-col md="2">
                  <BaseButton
                    type="button"
                    :disabled="invalid || loading"
                    variant="primary"
                    class="d-block w-100"
                    @click="next()"
                  >
                    Next 
                    <span
                      v-if="loading"
                      class="i-heroicons-arrow-path ml-2" 
                      animation="spin"
                    />
                  </BaseButton>
                </b-col>
              </b-row>
            </b-card>

            <b-card
              v-if="currentStep === 3"
              class="my-2"
            >
              <b-row>
                <h3>Step 3</h3>
              </b-row>

              <b-row>
                <b-col md="12">
                  <h5>Images</h5>
                </b-col>
                <b-col md="12">
                  <AppFileUpload
                    ref="fileUpload"
                    :storage-path="`public/${projectId}/documents/project-attachments/`"
                    @on-file-upload-completed="fileUploadCompleted($event)"
                  />
                </b-col>
                <b-col md="4">
                  <BaseButton
                    v-if="attachments.length > 0"
                    type="button"
                    variant="primary"
                    text="Cancel"
                    class="d-block w-100"
                    @click="removeFiles"
                  />
                </b-col>
              </b-row>

              <hr>
              <b-row align-h="end">
                <b-col md="2">
                  <BaseButton
                    type="submit"
                    variant="primary"
                    text="Submit"
                    class="d-block w-100"
                    :disabled="invalid"
                  />
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>
      </b-form>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { Project } from '@/models/project'
import AddressAutoComplete from '@/components/AddressAutoComplete.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseTextArea from '@/components/base/BaseTextArea.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import AppFileUpload from '@/components/app/AppFileUpload.vue'
import FirebaseService from '@/services/FirebaseService'
import { useAuth } from '@/composables/useAuth'
import { useRouter } from 'vue-router'
import { httpsCallable } from 'firebase/functions'
import StorageService from '@/services/StorageService'

const services = [
  'Interior Painting', 'Exterior Painting', 'Electrical', 'Landscaping',
  'Plumbing', 'Flooring', 'Roofing', 'Remodeling', 'Cleaning', 'Staging',
  'Photography', 'Emergencies'
]
const projectScops = []
const attachments = []
const currentStep = ref(1)
const projectId = ref('')
const project = new Project()
const loading = ref(false)
const { currentUser } = useAuth()
// const fileUploadRef = ref('fileUpload')

// const addProject = () => { }

const fileUploadCompleted = (url: string) => {
  attachments.push(url)
}

const fileClear = () => {
  // fileUploadRef.fileClear()
}


const removeFiles = async () => {
  const storageService = new StorageService()
  if (attachments.length > 0) {
    // eslint-disable-next-line prefer-const
    for (let i in attachments) {
      // eslint-disable-next-line prefer-const
      let attachment = attachments[i]
      await storageService.removeUploadedImages(attachment)
    }
    fileClear()
  }

  return true
}

const next = async () => {
  if (currentStep.value === 2) {
    if (project.address !== '') {
      const scopes = '\nProject scope ' + projectScops.join(',')
      project.description += scopes
      const result = await saveProject()
      // addProject(result.data.project)
      projectId.value = result.data.project.id
    } else {
      currentStep.value -= 1
    }
  }

  if (currentStep.value < 3) {
    currentStep.value += 1
  }
}

const back = () => {
  if (currentStep.value > 1) {
    currentStep.value -= 1
  }
}

const saveProject = async () => {
  loading.value = true
  const functions = (await FirebaseService.functions())
  const save = httpsCallable(functions, 'project-saveProjectV2')
  const response = await save({ id: currentUser.value.id, project: project })
  loading.value = false
  return response
}

const submit = async () => {
  // save project scope and attachments
  // flash('Project saved successfully!', 'success')
  useRouter().back()
}
</script>
